import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  campagnesApi, cotisationsApi,
  CAMPAGNE_CATEGORIES, CAMPAGNE_STATUSES, PAYMENT_METHODS, COT_STATUSES,
  CAT_LABEL, STATUS_LABEL, PM_LABEL, COT_LABEL, fmtFCFA,
  MONTHLY_DUES_MIN, currentMonth, fmtMonth,
} from '../../services/caisseApi';
import { getToken } from '../auth';
import './admin-form.css';

const EMPTY_CAMP = {
  title: '', slug: '',
  category: 'autre', status: 'active',
  shortDescription: '', description: '',
  goal: 0, raised: 0,
  startDate: '', endDate: '',
  beneficiary: '',
  mainImageSeed: '', mainImageUrl: '',
  featured: false, published: true, order: 0,
};

const EMPTY_COT = {
  campaignId: '',
  amount: 0,
  date: new Date().toISOString().slice(0, 10),
  donorName: '',
  anonymous: false,
  paymentMethod: 'especes',
  status: 'confirme',
  note: '',
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

const Caisse = () => {
  const token = getToken();
  const [tab, setTab] = useState('overview');

  if (!token) {
    return (
      <div className="content" style={{ padding: 40 }}>
        <p style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', color: 'var(--char-500)' }}>
          Session expirée. Reconnectez-vous.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="topbar">
        <div className="breadcrumb">
          <span>Cambérène</span><span className="sep">/</span>
          <span>Admin</span><span className="sep">/</span>
          <b>Caisse du village</b>
        </div>
        <div className="top-actions">
          <div style={{ display: 'flex', gap: 4, padding: 4, background: 'var(--bone-2)', flexWrap: 'wrap' }}>
            {[
              ['overview',   "Vue d'ensemble"],
              ['adhesions',  "Adhésions mensuelles"],
              ['campagnes',  "Projets"],
              ['cotisations',"Cotisations"],
            ].map(([k, lbl]) => (
              <button key={k} className="pill-btn"
                style={tab === k ? { background: 'var(--char-900)', color: 'var(--bone)' } : { background: 'transparent', border: 'none' }}
                onClick={() => setTab(k)}>{lbl}</button>
            ))}
          </div>
        </div>
      </div>
      {tab === 'overview'    && <OverviewPanel    token={token} onGoTo={setTab} />}
      {tab === 'adhesions'   && <AdhesionsPanel   token={token} />}
      {tab === 'campagnes'   && <CampagnesPanel   token={token} />}
      {tab === 'cotisations' && <CotisationsPanel token={token} />}
    </>
  );
};

/* ============================================================
   PANEL ADHÉSIONS MENSUELLES — grille membres × mois
   Règle : chaque membre doit payer 1000 FCFA min/mois via Wave
   ============================================================ */
const AdhesionsPanel = ({ token }) => {
  const [data, setData] = useState({ months: [], rows: [], stats: { totalMembers: 0, paidThisMonth: 0, fullyCompliant: 0, complianceRate: 0, currentMonth: currentMonth(), monthlyDuesMin: MONTHLY_DUES_MIN, requiredMethod: 'wave' } });
  const [loading, setLoading] = useState(true);
  const [monthsCount, setMonthsCount] = useState(6);
  const [filterText, setFilterText] = useState('');

  // Modal d'enregistrement rapide de cotisation
  const [quickForm, setQuickForm] = useState(null); // { memberId, fullName, month }
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [amountInput, setAmountInput] = useState(String(MONTHLY_DUES_MIN));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await cotisationsApi.adminDuesStatus(token, { months: monthsCount });
      setData(r);
    } catch (err) { alert(err.message); }
    finally       { setLoading(false); }
  }, [token, monthsCount]);
  useEffect(() => { load(); }, [load]);

  const filteredRows = useMemo(() => {
    if (!filterText) return data.rows;
    const q = filterText.toLowerCase();
    return data.rows.filter((r) => r.fullName.toLowerCase().includes(q) || r.email.toLowerCase().includes(q));
  }, [data.rows, filterText]);

  const openQuick = (memberId, fullName, month) => {
    setQuickForm({ memberId, fullName, month });
    setAmountInput(String(MONTHLY_DUES_MIN));
    setSaveError(null);
  };
  const closeQuick = () => setQuickForm(null);

  const onSubmitQuick = async (e) => {
    e.preventDefault();
    if (!quickForm) return;
    setSaving(true); setSaveError(null);
    try {
      const amt = parseInt(amountInput, 10) || 0;
      if (amt < MONTHLY_DUES_MIN) throw new Error(`Montant minimum : ${MONTHLY_DUES_MIN} FCFA`);
      await cotisationsApi.adminCreate(token, {
        memberId: quickForm.memberId,
        month: quickForm.month,
        amount: amt,
        donorName: quickForm.fullName,
        anonymous: false,
        paymentMethod: 'wave',
        status: 'confirme',
        date: new Date().toISOString().slice(0, 10),
        note: `Adhésion mensuelle ${quickForm.month}`,
      });
      closeQuick(); await load();
    } catch (err) { setSaveError(err.message); }
    finally       { setSaving(false); }
  };

  return (
    <div className="content">
      <div className="page-head">
        <div>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.22em', textTransform: 'uppercase', color: 'var(--char-500)' }}>
            Adhésions · {data.stats.totalMembers} membre{data.stats.totalMembers > 1 ? 's' : ''} actif{data.stats.totalMembers > 1 ? 's' : ''}
          </span>
          <h1>Cotisations mensuelles,<br /><span style={{ fontStyle: 'italic' }}>par membre.</span></h1>
          <p className="sub">
            <b>Règle :</b> {fmtFCFA(MONTHLY_DUES_MIN)} minimum par mois via <b>Wave</b>, confirmé.
            Cliquez sur une case pour enregistrer le paiement d'un mois.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="text" placeholder="Filtrer un membre…" value={filterText} onChange={(e) => setFilterText(e.target.value)}
            className="pill-btn" style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px', minWidth: 180 }} />
          <select value={monthsCount} onChange={(e) => setMonthsCount(parseInt(e.target.value, 10))}
            className="pill-btn" style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}>
            <option value={3}>3 derniers mois</option>
            <option value={6}>6 derniers mois</option>
            <option value={12}>12 derniers mois</option>
          </select>
          <button className="icon-btn" onClick={load} title="Rafraîchir">↻</button>
        </div>
      </div>

      {/* KPI conformité */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 28 }}>
        <div className="kpi">
          <span className="lab"><span className="dot" style={{ background: '#34A853' }} />Conformité ce mois</span>
          <b>{data.stats.complianceRate}%</b>
          <span className="delta">{data.stats.paidThisMonth} / {data.stats.totalMembers} payés</span>
        </div>
        <div className="kpi">
          <span className="lab"><span className="dot" style={{ background: '#E76F51' }} />À régulariser</span>
          <b>{data.stats.totalMembers - data.stats.paidThisMonth}</b>
          <span className="delta">membres en retard ({fmtMonth(data.stats.currentMonth)})</span>
        </div>
        <div className="kpi">
          <span className="lab"><span className="dot" style={{ background: '#C9A961' }} />Parfaitement à jour</span>
          <b>{data.stats.fullyCompliant}</b>
          <span className="delta">100% sur la période</span>
        </div>
        <div className="kpi" style={{ background: '#15110F', color: 'var(--bone)' }}>
          <span className="lab" style={{ color: 'rgba(250,246,238,.55)' }}>
            <span className="dot" style={{ background: '#1B6985' }} />Règle d'adhésion
          </span>
          <b style={{ color: 'var(--bone)', fontSize: 28 }}>
            {fmtFCFA(MONTHLY_DUES_MIN).replace(' FCFA', '')}
            <span className="unit" style={{ color: 'rgba(250,246,238,.55)', fontSize: '.5em', marginLeft: 4 }}>FCFA/mois</span>
          </b>
          <span className="delta" style={{ background: 'rgba(231,111,81,.2)', color: '#F4A28C' }}>via WAVE uniquement</span>
        </div>
      </div>

      {/* Grille membres × mois */}
      <div className="table-wrap" style={{ overflowX: 'auto' }}>
        <table className="table" style={{ minWidth: 720 }}>
          <thead>
            <tr>
              <th style={{ minWidth: 220 }}>Membre</th>
              {data.months.map((m) => (
                <th key={m} style={{ textAlign: 'center', minWidth: 64 }}>
                  {fmtMonth(m)}
                  {m === data.stats.currentMonth && (
                    <small style={{ display: 'block', fontSize: 9, color: 'var(--sunset)', letterSpacing: '.14em' }}>EN COURS</small>
                  )}
                </th>
              ))}
              <th style={{ textAlign: 'center' }}>Conformité</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={data.months.length + 2} style={{ textAlign: 'center', padding: 40, color: 'var(--char-500)' }}>Chargement…</td></tr>}
            {!loading && filteredRows.length === 0 && (
              <tr><td colSpan={data.months.length + 2} style={{ textAlign: 'center', padding: 40, color: 'var(--char-500)' }}>Aucun membre.</td></tr>
            )}
            {filteredRows.map((row) => (
              <tr key={row.memberId}>
                <td>
                  <b style={{ fontWeight: 500, fontSize: 14 }}>{row.fullName}</b>
                  <small style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--char-500)' }}>
                    {row.email} · {row.role}
                  </small>
                </td>
                {row.months.map((m) => (
                  <td key={m.month} style={{ textAlign: 'center', padding: 0 }}>
                    <button
                      onClick={() => !m.paid && openQuick(row.memberId, row.fullName, m.month)}
                      title={m.paid ? `Payé · ${fmtFCFA(m.amount)}` : `Enregistrer ${m.month}`}
                      style={{
                        width: '100%', height: 42, border: 'none',
                        background: m.paid ? 'rgba(52,168,83,.18)' : 'rgba(230,57,70,.06)',
                        color: m.paid ? '#1F7A3A' : '#B72333',
                        cursor: m.paid ? 'default' : 'pointer',
                        fontFamily: 'var(--mono)', fontSize: 14, fontWeight: 600,
                        transition: 'background .2s',
                      }}
                      onMouseEnter={(e) => { if (!m.paid) e.currentTarget.style.background = 'rgba(230,57,70,.14)'; }}
                      onMouseLeave={(e) => { if (!m.paid) e.currentTarget.style.background = 'rgba(230,57,70,.06)'; }}
                    >
                      {m.paid ? '✓' : '+'}
                    </button>
                  </td>
                ))}
                <td style={{ textAlign: 'center' }}>
                  <b style={{ fontFamily: 'var(--serif)', fontSize: 18, color: row.complianceRate === 100 ? '#1F7A3A' : row.complianceRate >= 50 ? 'var(--char-700)' : '#B72333' }}>
                    {row.complianceRate}%
                  </b>
                  <small style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--char-500)' }}>
                    {row.paidCount}/{data.months.length}
                  </small>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="table-foot">
          <span>{filteredRows.length} membre{filteredRows.length > 1 ? 's' : ''} affichés</span>
          <span>Période : {data.months.length > 0 ? `${fmtMonth(data.months[0])} → ${fmtMonth(data.months[data.months.length - 1])}` : '—'}</span>
        </div>
      </div>

      {/* Légende */}
      <div style={{ display: 'flex', gap: 24, marginTop: 18, fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.12em', color: 'var(--char-500)' }}>
        <span><span style={{ display: 'inline-block', width: 12, height: 12, background: 'rgba(52,168,83,.18)', color: '#1F7A3A', marginRight: 6, verticalAlign: 'middle', textAlign: 'center', lineHeight: '12px', fontSize: 9 }}>✓</span> Payé (≥ {fmtFCFA(MONTHLY_DUES_MIN)} via Wave, confirmé)</span>
        <span><span style={{ display: 'inline-block', width: 12, height: 12, background: 'rgba(230,57,70,.06)', color: '#B72333', marginRight: 6, verticalAlign: 'middle', textAlign: 'center', lineHeight: '12px', fontSize: 9 }}>+</span> En retard — cliquer pour enregistrer</span>
      </div>

      {/* Modal d'enregistrement rapide */}
      {quickForm && (
        <div className="admin-modal-bg" onClick={closeQuick}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="admin-modal-head">
              <div>
                <span className="admin-modal-eyebrow">Adhésion · {fmtMonth(quickForm.month)}</span>
                <h2 className="admin-modal-title">{quickForm.fullName}</h2>
              </div>
              <button onClick={closeQuick} className="admin-modal-close">✕</button>
            </div>
            <form className="admin-form" onSubmit={onSubmitQuick}>
              <p style={{ margin: '0 0 12px', fontSize: 13.5, color: 'var(--char-700)' }}>
                Enregistrer la cotisation Wave pour <b>{fmtMonth(quickForm.month)}</b>.
                Statut sera automatiquement <b>confirmé</b>.
              </p>
              <label className="admin-field">
                <span className="admin-field-label">Montant payé (FCFA) · min {MONTHLY_DUES_MIN}</span>
                <input type="number" min={MONTHLY_DUES_MIN} value={amountInput}
                  onChange={(e) => setAmountInput(e.target.value)} required autoFocus />
              </label>
              <div style={{ background: 'var(--bone-2)', padding: '10px 14px', fontSize: 12, color: 'var(--char-700)', fontFamily: 'var(--mono)', letterSpacing: '.08em' }}>
                Mode : <b>WAVE</b> · Mois : <b>{quickForm.month}</b>
              </div>
              {saveError && <div className="admin-form-error">{saveError}</div>}
              <div className="admin-form-foot">
                <button type="button" onClick={closeQuick} className="admin-btn">Annuler</button>
                <button type="submit" disabled={saving} className="admin-btn admin-btn-primary">
                  {saving ? 'Enregistrement…' : 'Enregistrer le paiement'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

/* ============================================================
   PANEL VUE D'ENSEMBLE — détails financiers complets de la caisse
   ============================================================ */
const OverviewPanel = ({ token, onGoTo }) => {
  const [campagnes, setCampagnes] = useState([]);
  const [cotisations, setCotisations] = useState([]);
  const [sumConfirmed, setSumConfirmed] = useState({ total: 0, count: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [campData, cotData] = await Promise.all([
        campagnesApi.adminList(token, { limit: 200 }),
        cotisationsApi.adminList(token, { limit: 200 }),
      ]);
      setCampagnes(campData.items);
      setCotisations(cotData.items);
      setSumConfirmed(cotData.sumConfirmed || { total: 0, count: 0 });
    } catch (err) { alert(err.message); }
    finally       { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  // KPI dérivés
  const stats = useMemo(() => {
    const totalGoal     = campagnes.reduce((s, c) => s + (c.goal || 0), 0);
    const totalRaised   = campagnes.reduce((s, c) => s + (c.raised || 0), 0);
    const activeCount   = campagnes.filter((c) => c.status === 'active').length;
    const reachedCount  = campagnes.filter((c) => c.status === 'atteinte').length;
    const totalContribs = cotisations.length;
    const confirmedC    = cotisations.filter((c) => c.status === 'confirme').length;
    const pendingC      = cotisations.filter((c) => c.status === 'en_attente').length;
    const avgContrib    = confirmedC > 0 ? Math.round(sumConfirmed.total / confirmedC) : 0;
    return {
      totalGoal, totalRaised, activeCount, reachedCount,
      totalContribs, confirmedC, pendingC, avgContrib,
      progressGlobal: totalGoal > 0 ? Math.round((totalRaised / totalGoal) * 100) : 0,
    };
  }, [campagnes, cotisations, sumConfirmed]);

  // Répartition par catégorie
  const byCategory = useMemo(() => {
    const map = {};
    for (const c of campagnes) {
      const k = c.category || 'autre';
      if (!map[k]) map[k] = { count: 0, goal: 0, raised: 0 };
      map[k].count++;
      map[k].goal   += c.goal   || 0;
      map[k].raised += c.raised || 0;
    }
    return Object.entries(map)
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => b.raised - a.raised);
  }, [campagnes]);

  // Répartition par mode de paiement (cotisations confirmées seulement)
  const byPayment = useMemo(() => {
    const map = {};
    for (const c of cotisations) {
      if (c.status !== 'confirme') continue;
      const k = c.paymentMethod || 'autre';
      if (!map[k]) map[k] = { count: 0, total: 0 };
      map[k].count++;
      map[k].total += c.amount || 0;
    }
    return Object.entries(map)
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => b.total - a.total);
  }, [cotisations]);

  // Top contributeurs (cotisations confirmées non anonymes, agrégées par nom)
  const topContributors = useMemo(() => {
    const map = {};
    for (const c of cotisations) {
      if (c.status !== 'confirme' || c.anonymous || !c.donorName) continue;
      const k = c.donorName.trim();
      if (!map[k]) map[k] = { count: 0, total: 0 };
      map[k].count++;
      map[k].total += c.amount || 0;
    }
    return Object.entries(map)
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 8);
  }, [cotisations]);

  // 5 dernières cotisations (toute statut)
  const recentActivity = useMemo(() => {
    return [...cotisations]
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 8);
  }, [cotisations]);

  const campMap = useMemo(() => Object.fromEntries(campagnes.map((c) => [c.id, c.title])), [campagnes]);

  if (loading) {
    return (
      <div className="content" style={{ padding: 40 }}>
        <p style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', color: 'var(--char-500)' }}>
          Chargement…
        </p>
      </div>
    );
  }

  return (
    <div className="content">
      <div className="page-head">
        <div>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.22em', textTransform: 'uppercase', color: 'var(--char-500)' }}>
            Caisse · Vue d'ensemble · {new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
          </span>
          <h1>L'argent du village,<br /><span style={{ fontStyle: 'italic' }}>en clair.</span></h1>
          <p className="sub">Tableau de bord financier complet : projets, cotisations, répartitions et contributeurs.</p>
        </div>
      </div>

      {/* ===== 4 KPI principaux ===== */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 28 }}>
        <div className="kpi" style={{ background: '#15110F', color: 'var(--bone)', borderColor: 'transparent' }}>
          <span className="lab" style={{ color: 'rgba(250,246,238,.55)' }}>
            <span className="dot" style={{ background: '#E76F51' }} />
            Total collecté
          </span>
          <b style={{ color: 'var(--bone)' }}>{fmtFCFA(stats.totalRaised).replace(' FCFA', '')}<span className="unit" style={{ color: 'rgba(250,246,238,.55)' }}> FCFA</span></b>
          <span className="delta up" style={{ background: 'rgba(52,168,83,.2)', color: '#7CE39A' }}>
            ▲ {stats.progressGlobal}% de l'objectif global
          </span>
        </div>
        <div className="kpi">
          <span className="lab"><span className="dot" style={{ background: '#1B6985' }} />Objectif global</span>
          <b>{fmtFCFA(stats.totalGoal).replace(' FCFA', '')}<span className="unit"> FCFA</span></b>
          <span className="delta">{stats.activeCount} projet{stats.activeCount > 1 ? 's' : ''} actif{stats.activeCount > 1 ? 's' : ''}</span>
        </div>
        <div className="kpi">
          <span className="lab"><span className="dot" style={{ background: '#34A853' }} />Contributeurs</span>
          <b>{stats.confirmedC}</b>
          <span className="delta">{stats.pendingC > 0 ? `· ${stats.pendingC} en attente` : '· tous confirmés'}</span>
        </div>
        <div className="kpi">
          <span className="lab"><span className="dot" style={{ background: '#C9A961' }} />Cotisation moyenne</span>
          <b>{fmtFCFA(stats.avgContrib).replace(' FCFA', '')}<span className="unit"> FCFA</span></b>
          <span className="delta">par contribution confirmée</span>
        </div>
      </div>

      {/* ===== Projets actifs avec barres de progression ===== */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 18, marginBottom: 28 }}>
        <div style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
            <h3 style={{ fontFamily: 'var(--serif)', fontWeight: 400, fontSize: 22, margin: 0 }}>Projets en cours</h3>
            <button onClick={() => onGoTo('campagnes')} className="pill-btn">+ Nouveau projet</button>
          </div>
          {campagnes.filter((c) => c.status === 'active').length === 0 && (
            <p style={{ color: 'var(--char-500)', fontStyle: 'italic', fontSize: 13 }}>Aucun projet actif. Démarrez-en un dans l'onglet « Projets ».</p>
          )}
          {campagnes.filter((c) => c.status === 'active').map((c) => (
            <div key={c.id} style={{ paddingBottom: 16, marginBottom: 16, borderBottom: '1px solid var(--line)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                <b style={{ fontWeight: 500, fontSize: 14 }}>{c.title}</b>
                <span style={{ fontFamily: 'var(--serif)', fontSize: 20 }}>{c.progress}%</span>
              </div>
              <div style={{ height: 6, background: 'var(--bone-2)', position: 'relative', marginBottom: 6 }}>
                <div style={{ position: 'absolute', inset: '0 auto 0 0', width: `${c.progress}%`, background: '#E76F51', transition: 'width .8s' }} />
              </div>
              <small style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-500)' }}>
                {fmtFCFA(c.raised)} <b style={{ color: 'var(--char-700)' }}>/</b> {fmtFCFA(c.goal)}
                {c.daysLeft != null && c.daysLeft > 0 && ` · ${c.daysLeft}j restants`}
              </small>
            </div>
          ))}
        </div>

        {/* Répartition par catégorie */}
        <div style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: 24 }}>
          <h3 style={{ fontFamily: 'var(--serif)', fontWeight: 400, fontSize: 22, margin: '0 0 18px' }}>Par catégorie</h3>
          {byCategory.length === 0 && <p style={{ color: 'var(--char-500)', fontStyle: 'italic', fontSize: 13 }}>—</p>}
          {byCategory.map((cat) => {
            const pct = stats.totalRaised > 0 ? Math.round((cat.raised / stats.totalRaised) * 100) : 0;
            return (
              <div key={cat.id} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                  <span style={{ fontWeight: 500 }}>{CAT_LABEL[cat.id]}</span>
                  <span style={{ fontFamily: 'var(--mono)', color: 'var(--char-500)' }}>{cat.count} · {pct}%</span>
                </div>
                <div style={{ height: 4, background: 'var(--bone-2)', position: 'relative' }}>
                  <div style={{ position: 'absolute', inset: '0 auto 0 0', width: `${pct}%`, background: 'var(--char-900)' }} />
                </div>
                <small style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--char-500)' }}>{fmtFCFA(cat.raised)}</small>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== 2 colonnes : Top contributeurs + Modes paiement ===== */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 28 }}>
        <div style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: 24 }}>
          <h3 style={{ fontFamily: 'var(--serif)', fontWeight: 400, fontSize: 22, margin: '0 0 18px' }}>Top contributeurs</h3>
          {topContributors.length === 0 && <p style={{ color: 'var(--char-500)', fontStyle: 'italic', fontSize: 13 }}>Aucun contributeur identifié (toutes anonymes ?)</p>}
          {topContributors.map((p, i) => (
            <div key={p.name} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
              padding: '10px 0', borderBottom: i < topContributors.length - 1 ? '1px solid var(--line)' : 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{
                  width: 28, height: 28,
                  background: 'linear-gradient(135deg, #E76F51, #C9A961)',
                  color: 'var(--char-900)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 600,
                }}>{String(i + 1).padStart(2, '0')}</span>
                <div>
                  <b style={{ fontWeight: 500, fontSize: 14 }}>{p.name}</b>
                  <small style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--char-500)' }}>
                    {p.count} cotisation{p.count > 1 ? 's' : ''}
                  </small>
                </div>
              </div>
              <b style={{ fontFamily: 'var(--serif)', fontSize: 17, color: 'var(--char-900)' }}>{fmtFCFA(p.total)}</b>
            </div>
          ))}
        </div>

        <div style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: 24 }}>
          <h3 style={{ fontFamily: 'var(--serif)', fontWeight: 400, fontSize: 22, margin: '0 0 18px' }}>Modes de paiement</h3>
          {byPayment.length === 0 && <p style={{ color: 'var(--char-500)', fontStyle: 'italic', fontSize: 13 }}>—</p>}
          {byPayment.map((p) => {
            const pct = sumConfirmed.total > 0 ? Math.round((p.total / sumConfirmed.total) * 100) : 0;
            return (
              <div key={p.id} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                  <span style={{ fontWeight: 500 }}>{PM_LABEL[p.id] || p.id}</span>
                  <span style={{ fontFamily: 'var(--mono)', color: 'var(--char-500)' }}>{p.count} · {pct}%</span>
                </div>
                <div style={{ height: 4, background: 'var(--bone-2)', position: 'relative' }}>
                  <div style={{ position: 'absolute', inset: '0 auto 0 0', width: `${pct}%`, background: '#1B6985' }} />
                </div>
                <small style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--char-500)' }}>{fmtFCFA(p.total)}</small>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== Activité récente ===== */}
      <div style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h3 style={{ fontFamily: 'var(--serif)', fontWeight: 400, fontSize: 22, margin: 0 }}>Activité récente</h3>
          <button onClick={() => onGoTo('cotisations')} className="pill-btn">Voir toutes</button>
        </div>
        {recentActivity.length === 0 && <p style={{ color: 'var(--char-500)', fontStyle: 'italic', fontSize: 13 }}>Aucune cotisation enregistrée.</p>}
        <table className="table" style={{ marginTop: 8 }}>
          <thead>
            <tr><th>Date</th><th>Donateur</th><th>Projet</th><th>Mode</th><th style={{ textAlign: 'right' }}>Montant</th><th>Statut</th></tr>
          </thead>
          <tbody>
            {recentActivity.map((c) => (
              <tr key={c.id}>
                <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-500)' }}>{fmtDate(c.date)}</td>
                <td><b style={{ fontWeight: 500 }}>{c.donorName || (c.anonymous ? '(Anonyme)' : '—')}</b></td>
                <td style={{ fontSize: 13, color: 'var(--char-700)' }}>{c.campaignId ? campMap[c.campaignId] || '(supprimée)' : <em style={{ color: 'var(--char-500)' }}>Libre</em>}</td>
                <td><span className="cat-pill">{PM_LABEL[c.paymentMethod] || '—'}</span></td>
                <td style={{ textAlign: 'right' }}><b>{fmtFCFA(c.amount)}</b></td>
                <td><span className={`status-pill ${c.status === 'confirme' ? 'live' : c.status === 'en_attente' ? 'warn' : 'err'}`}>{COT_LABEL[c.status]}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ============================================================
   PANEL CAMPAGNES
   ============================================================ */
const CampagnesPanel = ({ token }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [query, setQuery] = useState('');

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_CAMP);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await campagnesApi.adminList(token, {
        category: filterCategory !== 'all' ? filterCategory : undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        q: query || undefined,
        limit: 200,
      });
      setItems(data.items); setTotal(data.total);
    } catch (err) { alert(err.message); }
    finally       { setLoading(false); }
  }, [token, filterCategory, filterStatus, query]);
  useEffect(() => { load(); }, [load]);

  const kpi = useMemo(() => {
    const c = { total: items.length, active: 0, atteinte: 0, raised: 0, goal: 0 };
    for (const it of items) {
      if (it.status === 'active')    c.active++;
      if (it.status === 'atteinte')  c.atteinte++;
      c.raised += it.raised || 0;
      c.goal   += it.goal   || 0;
    }
    return c;
  }, [items]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_CAMP, startDate: new Date().toISOString().slice(0, 10) });
    setSaveError(null); setOpen(true);
  };
  const openEdit = async (id) => {
    setEditingId(id); setSaveError(null);
    try {
      const { campagne } = await campagnesApi.adminGet(token, id);
      setForm({
        title: campagne.title, slug: campagne.slug,
        category: campagne.category, status: campagne.status,
        shortDescription: campagne.shortDescription || '', description: campagne.description || '',
        goal: campagne.goal || 0, raised: campagne.raised || 0,
        startDate: campagne.startDate ? campagne.startDate.slice(0, 10) : '',
        endDate:   campagne.endDate   ? campagne.endDate.slice(0, 10)   : '',
        beneficiary: campagne.beneficiary || '',
        mainImageSeed: campagne.mainImageSeed || '', mainImageUrl: campagne.mainImageUrl || '',
        featured: campagne.featured, published: campagne.published, order: campagne.order || 0,
      });
      setOpen(true);
    } catch (err) { alert(err.message); }
  };
  const close = useCallback(() => { setOpen(false); setEditingId(null); setForm(EMPTY_CAMP); setSaveError(null); }, []);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', onKey); };
  }, [open, close]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setSaveError(null);
    try {
      const payload = {
        ...form,
        slug: form.slug || undefined,
        goal: parseInt(form.goal, 10) || 0,
        raised: parseInt(form.raised, 10) || 0,
        order: parseInt(form.order, 10) || 0,
      };
      if (editingId) await campagnesApi.adminUpdate(token, editingId, payload);
      else           await campagnesApi.adminCreate(token, payload);
      close(); await load();
    } catch (err) { setSaveError(err.message); }
    finally       { setSaving(false); }
  };
  const onDelete = async (id, title) => {
    if (!window.confirm(`Supprimer la campagne « ${title} » ?`)) return;
    try { await campagnesApi.adminDelete(token, id); await load(); }
    catch (err) { alert(err.message); }
  };

  return (
    <div className="content">
      <div className="page-head">
        <div>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.22em', textTransform: 'uppercase', color: 'var(--char-500)' }}>
            Campagnes · {total}
          </span>
          <h1>Les chantiers,<br /><span style={{ fontStyle: 'italic' }}>en financement.</span></h1>
          <p className="sub">Créer et suivre les campagnes de la caisse du village. Les compteurs `raised` sont calculés automatiquement.</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="text" placeholder="Chercher…" value={query} onChange={(e) => setQuery(e.target.value)}
            className="pill-btn" style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px', minWidth: 180 }} />
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
            className="pill-btn" style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}>
            {CAMPAGNE_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.lbl}</option>)}
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            className="pill-btn" style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}>
            {CAMPAGNE_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.lbl}</option>)}
          </select>
          <button className="top-cta" onClick={openCreate}><span className="plus" /> Nouvelle campagne</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
        {[
          { lbl: 'Total',     val: kpi.total,     color: 'var(--char-400)' },
          { lbl: 'En cours',  val: kpi.active,    color: '#E76F51' },
          { lbl: 'Atteintes', val: kpi.atteinte,  color: '#34A853' },
          { lbl: 'Collecté',  val: fmtFCFA(kpi.raised), color: '#C9A961', isText: true },
        ].map((k) => (
          <div key={k.lbl} className="kpi">
            <span className="lab"><span className="dot" style={{ background: k.color }} />{k.lbl}</span>
            <b style={k.isText ? { fontSize: 22 } : {}}>{k.val}</b>
          </div>
        ))}
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Titre</th>
              <th>Catégorie</th>
              <th>Statut</th>
              <th>Progression</th>
              <th>★</th>
              <th style={{ width: 120 }} />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--char-500)' }}>Chargement…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--char-500)' }}>Aucune campagne.</td></tr>}
            {items.map((c) => (
              <tr key={c.id} onClick={() => openEdit(c.id)}>
                <td>
                  <b style={{ fontWeight: 500, fontSize: 14 }}>{c.title}</b>
                  <small style={{ display: 'block', marginTop: 2, fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--char-500)' }}>/{c.slug}</small>
                </td>
                <td><span className="cat-pill">{CAT_LABEL[c.category]}</span></td>
                <td><span className={`status-pill ${c.status === 'atteinte' ? 'live' : c.status === 'active' ? 'warn' : 'muted'}`}>{STATUS_LABEL[c.status]}</span></td>
                <td>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 11, marginBottom: 4 }}>
                    {fmtFCFA(c.raised)} / {fmtFCFA(c.goal)} <b>· {c.progress}%</b>
                  </div>
                  <div style={{ height: 4, background: 'var(--bone-2)', position: 'relative' }}>
                    <div style={{ position: 'absolute', inset: '0 auto 0 0', width: `${c.progress}%`, background: '#E76F51' }} />
                  </div>
                </td>
                <td>{c.featured && <span style={{ color: 'var(--sunset)' }}>★</span>}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div className="row-actions" style={{ opacity: 1 }}>
                    <button title="Éditer" onClick={() => openEdit(c.id)}>✎</button>
                    <button title="Supprimer" className="danger" onClick={() => onDelete(c.id, c.title)}>✕</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="table-foot"><span>{total} campagne{total > 1 ? 's' : ''}</span></div>
      </div>

      {open && (
        <div className="admin-modal-bg" onClick={close}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760 }}>
            <div className="admin-modal-head">
              <div>
                <span className="admin-modal-eyebrow">{editingId ? 'Édition' : 'Nouvelle campagne'}</span>
                <h2 className="admin-modal-title">{editingId ? form.title : 'Créer une campagne'}</h2>
              </div>
              <button onClick={close} className="admin-modal-close">✕</button>
            </div>
            <form className="admin-form" onSubmit={onSubmit}>
              <div className="admin-form-row" style={{ gridTemplateColumns: '1.4fr 1fr' }}>
                <label className="admin-field">
                  <span className="admin-field-label">Titre *</span>
                  <input type="text" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required />
                </label>
                <label className="admin-field">
                  <span className="admin-field-label">Catégorie *</span>
                  <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                    {CAMPAGNE_CATEGORIES.filter((c) => c.id !== 'all').map((c) => <option key={c.id} value={c.id}>{c.lbl}</option>)}
                  </select>
                </label>
              </div>
              <label className="admin-field">
                <span className="admin-field-label">Slug (vide = auto)</span>
                <input type="text" value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} />
              </label>
              <label className="admin-field">
                <span className="admin-field-label">Description courte</span>
                <textarea rows={2} value={form.shortDescription} onChange={(e) => setForm((f) => ({ ...f, shortDescription: e.target.value }))} />
              </label>
              <label className="admin-field">
                <span className="admin-field-label">Description complète</span>
                <textarea rows={4} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
              </label>
              <div className="admin-form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                <label className="admin-field">
                  <span className="admin-field-label">Objectif (FCFA) *</span>
                  <input type="number" min="0" value={form.goal} onChange={(e) => setForm((f) => ({ ...f, goal: e.target.value }))} required />
                </label>
                <label className="admin-field">
                  <span className="admin-field-label">Collecté (auto)</span>
                  <input type="number" min="0" value={form.raised} onChange={(e) => setForm((f) => ({ ...f, raised: e.target.value }))} />
                </label>
                <label className="admin-field">
                  <span className="admin-field-label">Statut</span>
                  <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                    {CAMPAGNE_STATUSES.filter((s) => s.id !== 'all').map((s) => <option key={s.id} value={s.id}>{s.lbl}</option>)}
                  </select>
                </label>
              </div>
              <div className="admin-form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <label className="admin-field">
                  <span className="admin-field-label">Début</span>
                  <input type="date" value={form.startDate} onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))} />
                </label>
                <label className="admin-field">
                  <span className="admin-field-label">Fin (optionnel)</span>
                  <input type="date" value={form.endDate} onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))} />
                </label>
              </div>
              <label className="admin-field">
                <span className="admin-field-label">Bénéficiaire</span>
                <input type="text" value={form.beneficiary} onChange={(e) => setForm((f) => ({ ...f, beneficiary: e.target.value }))} placeholder="École Layène · 160 enfants" />
              </label>
              <div className="admin-form-row" style={{ gridTemplateColumns: '1fr 1.4fr' }}>
                <label className="admin-field">
                  <span className="admin-field-label">Image seed</span>
                  <input type="text" value={form.mainImageSeed} onChange={(e) => setForm((f) => ({ ...f, mainImageSeed: e.target.value }))} />
                </label>
                <label className="admin-field">
                  <span className="admin-field-label">Ou URL image</span>
                  <input type="url" value={form.mainImageUrl} onChange={(e) => setForm((f) => ({ ...f, mainImageUrl: e.target.value }))} />
                </label>
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <label className="admin-publish-toggle">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} />
                  <span>{form.featured ? '★ En vedette' : 'Mettre en vedette'}</span>
                </label>
                <label className="admin-publish-toggle">
                  <input type="checkbox" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} />
                  <span>{form.published ? 'Publié' : 'Brouillon'}</span>
                </label>
              </div>
              {saveError && <div className="admin-form-error">{saveError}</div>}
              <div className="admin-form-foot">
                <button type="button" onClick={close} className="admin-btn">Annuler</button>
                <button type="submit" disabled={saving} className="admin-btn admin-btn-primary">
                  {saving ? 'Enregistrement…' : (editingId ? 'Enregistrer' : 'Créer')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

/* ============================================================
   PANEL COTISATIONS
   ============================================================ */
const CotisationsPanel = ({ token }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [sumConfirmed, setSumConfirmed] = useState({ total: 0, count: 0 });
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCampaign, setFilterCampaign] = useState('');

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_COT);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cotsData, campData] = await Promise.all([
        cotisationsApi.adminList(token, {
          status: filterStatus !== 'all' ? filterStatus : undefined,
          campaignId: filterCampaign || undefined,
          limit: 200,
        }),
        campagnesApi.adminList(token, { limit: 100 }),
      ]);
      setItems(cotsData.items);
      setTotal(cotsData.total);
      setSumConfirmed(cotsData.sumConfirmed || { total: 0, count: 0 });
      setCampaigns(campData.items);
    } catch (err) { alert(err.message); }
    finally       { setLoading(false); }
  }, [token, filterStatus, filterCampaign]);
  useEffect(() => { load(); }, [load]);

  const campMap = useMemo(() => Object.fromEntries(campaigns.map((c) => [c.id, c.title])), [campaigns]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_COT); setSaveError(null); setOpen(true); };
  const openEdit = async (id) => {
    setEditingId(id); setSaveError(null);
    try {
      const { cotisation } = await cotisationsApi.adminGet(token, id);
      setForm({
        campaignId: cotisation.campaignId || '',
        amount: cotisation.amount,
        date: cotisation.date ? cotisation.date.slice(0, 10) : '',
        donorName: cotisation.donorName || '',
        anonymous: cotisation.anonymous,
        paymentMethod: cotisation.paymentMethod,
        status: cotisation.status,
        note: cotisation.note || '',
      });
      setOpen(true);
    } catch (err) { alert(err.message); }
  };
  const close = useCallback(() => { setOpen(false); setEditingId(null); setForm(EMPTY_COT); setSaveError(null); }, []);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', onKey); };
  }, [open, close]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setSaveError(null);
    try {
      const payload = {
        ...form,
        campaignId: form.campaignId || null,
        amount: parseInt(form.amount, 10) || 0,
      };
      if (editingId) await cotisationsApi.adminUpdate(token, editingId, payload);
      else           await cotisationsApi.adminCreate(token, payload);
      close(); await load();
    } catch (err) { setSaveError(err.message); }
    finally       { setSaving(false); }
  };
  const onDelete = async (id) => {
    if (!window.confirm('Supprimer cette cotisation ? Le compteur de la campagne sera ajusté.')) return;
    try { await cotisationsApi.adminDelete(token, id); await load(); }
    catch (err) { alert(err.message); }
  };

  return (
    <div className="content">
      <div className="page-head">
        <div>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.22em', textTransform: 'uppercase', color: 'var(--char-500)' }}>
            Cotisations · {total} · {fmtFCFA(sumConfirmed.total)} confirmés
          </span>
          <h1>Les contributions,<br /><span style={{ fontStyle: 'italic' }}>une par une.</span></h1>
          <p className="sub">Enregistrer chaque cotisation. Confirmation = incrément automatique du compteur de la campagne liée.</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={filterCampaign} onChange={(e) => setFilterCampaign(e.target.value)}
            className="pill-btn" style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px', minWidth: 180 }}>
            <option value="">Toutes campagnes</option>
            {campaigns.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            className="pill-btn" style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}>
            {COT_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.lbl}</option>)}
          </select>
          <button className="top-cta" onClick={openCreate}><span className="plus" /> Nouvelle cotisation</button>
        </div>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Donateur</th>
              <th>Campagne</th>
              <th>Montant</th>
              <th>Paiement</th>
              <th>Statut</th>
              <th style={{ width: 120 }} />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--char-500)' }}>Chargement…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--char-500)' }}>Aucune cotisation.</td></tr>}
            {items.map((c) => (
              <tr key={c.id} onClick={() => openEdit(c.id)}>
                <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-500)' }}>{fmtDate(c.date)}</td>
                <td>
                  <b style={{ fontWeight: 500 }}>{c.donorName || (c.anonymous ? '(Anonyme)' : '—')}</b>
                  {c.anonymous && <small style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--sunset)' }}>ANONYME</small>}
                </td>
                <td style={{ fontSize: 13, color: 'var(--char-700)' }}>
                  {c.campaignId ? campMap[c.campaignId] || '(supprimée)' : <em style={{ color: 'var(--char-500)' }}>Libre affectation</em>}
                </td>
                <td><b>{fmtFCFA(c.amount)}</b></td>
                <td><span className="cat-pill">{PM_LABEL[c.paymentMethod]}</span></td>
                <td>
                  <span className={`status-pill ${c.status === 'confirme' ? 'live' : c.status === 'en_attente' ? 'warn' : 'err'}`}>
                    {COT_LABEL[c.status]}
                  </span>
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div className="row-actions" style={{ opacity: 1 }}>
                    <button title="Éditer" onClick={() => openEdit(c.id)}>✎</button>
                    <button title="Supprimer" className="danger" onClick={() => onDelete(c.id)}>✕</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="table-foot">
          <span>{total} cotisation{total > 1 ? 's' : ''}</span>
          <span>Total confirmé : {fmtFCFA(sumConfirmed.total)}</span>
        </div>
      </div>

      {open && (
        <div className="admin-modal-bg" onClick={close}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <div className="admin-modal-head">
              <div>
                <span className="admin-modal-eyebrow">{editingId ? 'Édition' : 'Nouvelle cotisation'}</span>
                <h2 className="admin-modal-title">Enregistrer une contribution</h2>
              </div>
              <button onClick={close} className="admin-modal-close">✕</button>
            </div>
            <form className="admin-form" onSubmit={onSubmit}>
              <label className="admin-field">
                <span className="admin-field-label">Campagne (laisser vide = libre affectation)</span>
                <select value={form.campaignId} onChange={(e) => setForm((f) => ({ ...f, campaignId: e.target.value }))}>
                  <option value="">— Libre affectation —</option>
                  {campaigns.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
              </label>
              <div className="admin-form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <label className="admin-field">
                  <span className="admin-field-label">Montant (FCFA) *</span>
                  <input type="number" min="1" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} required />
                </label>
                <label className="admin-field">
                  <span className="admin-field-label">Date</span>
                  <input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
                </label>
              </div>
              <label className="admin-field">
                <span className="admin-field-label">Nom du donateur (vide si anonyme)</span>
                <input type="text" value={form.donorName} onChange={(e) => setForm((f) => ({ ...f, donorName: e.target.value }))} />
              </label>
              <label className="admin-publish-toggle">
                <input type="checkbox" checked={form.anonymous} onChange={(e) => setForm((f) => ({ ...f, anonymous: e.target.checked }))} />
                <span>{form.anonymous ? 'Affiché en anonyme' : 'Nom visible publiquement'}</span>
              </label>
              <div className="admin-form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <label className="admin-field">
                  <span className="admin-field-label">Mode de paiement</span>
                  <select value={form.paymentMethod} onChange={(e) => setForm((f) => ({ ...f, paymentMethod: e.target.value }))}>
                    {PAYMENT_METHODS.map((p) => <option key={p.id} value={p.id}>{p.lbl}</option>)}
                  </select>
                </label>
                <label className="admin-field">
                  <span className="admin-field-label">Statut</span>
                  <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                    {COT_STATUSES.filter((s) => s.id !== 'all').map((s) => <option key={s.id} value={s.id}>{s.lbl}</option>)}
                  </select>
                </label>
              </div>
              <label className="admin-field">
                <span className="admin-field-label">Note interne</span>
                <textarea rows={2} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
              </label>
              {saveError && <div className="admin-form-error">{saveError}</div>}
              <div className="admin-form-foot">
                <button type="button" onClick={close} className="admin-btn">Annuler</button>
                <button type="submit" disabled={saving} className="admin-btn admin-btn-primary">
                  {saving ? 'Enregistrement…' : (editingId ? 'Enregistrer' : 'Créer')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Caisse;
